import axios from 'axios';
import { supabase } from '../lib/supabase';
import { apiBaseUrl } from './portal';

const API_BASE_URL = apiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  // A hung or cold-starting backend must fail the request rather than hang
  // forever. 30s is generous enough for the heaviest compute endpoints
  // (a 200x200 grid is ~2.5s) while still bounding a stuck request.
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * The instance default `Content-Type: application/json` makes axios JSON-encode a
 * FormData body (the server then sees no form fields). For FormData, drop the header
 * so the browser sets `multipart/form-data` with the right boundary.
 */
export function prepareFormDataConfig(config) {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type');
    } else if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }
  return config;
}

api.interceptors.request.use(prepareFormDataConfig);

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

/**
 * GET dedupe: identical GETs (same URL + params) that overlap in time share one request, and a
 * settled response is reused for GET_CACHE_TTL_MS. Several hooks/contexts fetch the same resource
 * on a page load (dataset record, profile, organizations); this collapses them to one round trip.
 * - `api.get(url, { fresh: true })` bypasses the cache and the in-flight share.
 * - Any non-GET clears the cache, so a read after a write is never stale.
 * - Requests with an AbortSignal are never shared (one caller aborting must not cancel another).
 * Callers share the same response object, so treat `res.data` as read-only.
 */
export const GET_CACHE_TTL_MS = 2000;
const getCache = new Map(); // key -> { promise, expires (Infinity while pending) }

export function clearGetCache() {
  getCache.clear();
}

function getKey(url, config) {
  const { params, responseType, baseURL } = config;
  const sorted = params ? Object.keys(params).sort().map((k) => [k, params[k]]) : null;
  return JSON.stringify([url, sorted, responseType || '', baseURL || '']);
}

const rawGet = api.get.bind(api);
api.get = (url, config = {}) => {
  const { fresh, ...axiosConfig } = config || {};
  if (fresh || axiosConfig.signal || axiosConfig.cancelToken) return rawGet(url, axiosConfig);
  const key = getKey(url, axiosConfig);
  const hit = getCache.get(key);
  if (hit && hit.expires > Date.now()) return hit.promise;
  const entry = { promise: null, expires: Infinity };
  entry.promise = rawGet(url, axiosConfig).then(
    (res) => {
      if (getCache.get(key) === entry) entry.expires = Date.now() + GET_CACHE_TTL_MS;
      return res;
    },
    (err) => {
      if (getCache.get(key) === entry) getCache.delete(key);
      throw err;
    },
  );
  getCache.set(key, entry);
  return entry.promise;
};

['post', 'put', 'patch', 'delete'].forEach((method) => {
  const raw = api[method].bind(api);
  api[method] = (...args) => {
    clearGetCache();
    // Clear again once settled: a GET issued while the write was in flight may have read old data.
    return raw(...args).finally(clearGetCache);
  };
});

export default api;

// Page size the backend defaults to for survey-lines / soundings / anomalies.
const PAGE_SIZE = 5000;
// Raw pseudosection pages (backend default limit; max 200k).
const RAW_PAGE_SIZE = 50000;

/**
 * Fetch every page of a paginated endpoint (limit/offset) until a short page
 * comes back, so callers still receive the full result as one response.
 * `merge(pages)` combines the page payloads into the single `data` value.
 */
async function fetchAllPages(url, params, merge, pageLength, pageSize = PAGE_SIZE) {
  const pages = [];
  for (let offset = 0; ; offset += pageSize) {
    const res = await api.get(url, { params: { ...params, limit: pageSize, offset } });
    pages.push(res.data);
    if (pageLength(res.data) < pageSize) return { ...res, data: merge(pages) };
  }
}

const listAll = (url, projectId) =>
  fetchAllPages(url, { project_id: projectId }, (pages) => pages.flat(), (page) => page.length);

const geojsonAll = (url, projectId) =>
  fetchAllPages(
    url,
    { project_id: projectId },
    (pages) => ({ ...pages[0], features: pages.flatMap((p) => p.features) }),
    (page) => page.features.length,
  );

// API methods
export const organizationsApi = {
  create: (data) => api.post('/api/organizations', data),
  list: () => api.get('/api/organizations'),
  members: (orgId) => api.get(`/api/organizations/${orgId}/members`),
  usage: (orgId) => api.get('/api/usage', { params: { organization_id: orgId } }),
  // Owner only. force: also delete the org's projects and datasets (409 without it while projects exist).
  remove: (orgId, { force = false } = {}) => api.delete(`/api/organizations/${orgId}`, { params: force ? { force: 1 } : undefined }),
  // Any non-owner member can leave.
  leave: (orgId) => api.delete(`/api/organizations/${orgId}/members/me`),
};

export const projectsApi = {
  create: (data, organizationId) => api.post('/api/projects', data, { params: { organization_id: organizationId } }),
  list: (organizationId) => api.get('/api/projects', { params: { organization_id: organizationId } }),
  get: (projectId) => api.get(`/api/projects/${projectId}`),
  update: (projectId, data) => api.patch(`/api/projects/${projectId}`, data),
  // force: also delete the project's datasets (the API answers 409 without it).
  remove: (projectId, { force = false } = {}) => api.delete(`/api/projects/${projectId}`, { params: force ? { force: 1 } : undefined }),
};

export const datasetsApi = {
  create: (data) => api.post('/api/datasets', data),
  list: (params) => api.get('/api/datasets', { params }),
  get: (datasetId) => api.get(`/api/datasets/${datasetId}`),
  requestUploadUrl: (data) => api.post('/api/upload/request-url', data),
  completeUpload: (data) => api.post('/api/upload/complete', data),  // FormData: the request interceptor drops the JSON Content-Type so it goes out as multipart
  // { project_id } moves the dataset to another project of its organization; { name } renames it.
  update: (datasetId, body) => api.patch(`/api/datasets/${datasetId}`, body),
  delete: (datasetId) => api.delete(`/api/datasets/${datasetId}`),
  getDownloadUrl: (datasetId) => api.get(`/api/download/${datasetId}`),
  // Parsed XYZ points; the server caps a response (default 200k) and reports
  // `total` / `truncated` — pass { limit, offset } to page.
  points: (datasetId, params) => api.get(`/api/datasets/${datasetId}/points`, { params }),
  // Saved X/Y/Z column mapping + CRS of a CSV/XYZ dataset (read back as `column_mapping` on the dataset).
  saveColumnMapping: (datasetId, mapping) => api.post(`/api/datasets/${datasetId}/column-mapping`, mapping),
  updateQcStatus: (datasetId, data) => api.put(`/api/datasets/${datasetId}/qc-status`, data),
};

// Raw resistivity/IP field files (backend/formats): detect, parse, and read back a stored survey.
export const rawApi = {
  // Either a File (multipart) or { dataset_id } for an already-uploaded dataset.
  detect: (fileOrBody) => {
    if (typeof File !== 'undefined' && fileOrBody instanceof File) {
      const form = new FormData();
      form.append('file', fileOrBody);
      return api.post('/api/raw/detect', form);
    }
    return api.post('/api/raw/detect', fileOrBody);
  },
  parse: (datasetId) => api.post(`/api/datasets/${datasetId}/parse-raw`),
  // GeoJSON FeatureCollection: WGS84 lon/lat (properties.crs === 'EPSG:4326') when the
  // dataset has metadata.georef, else LOCAL survey coordinates (properties.crs === 'local').
  // Pass { local: true } to force the local frame.
  electrodes: (datasetId, params) => api.get(`/api/datasets/${datasetId}/raw/electrodes`, { params }),
  // Pseudosection points; pass { limit, offset } to page (response has `total` / `truncated`).
  pseudosection: (datasetId, params) => api.get(`/api/datasets/${datasetId}/raw/pseudosection`, { params }),
  // Every page of the pseudosection merged into one FeatureCollection. Pages are large
  // (50k) because each request re-runs the parse (and the QC when qc=1).
  pseudosectionAll: (datasetId, params) =>
    fetchAllPages(
      `/api/datasets/${datasetId}/raw/pseudosection`,
      params,
      (pages) => ({ ...pages[0], features: pages.flatMap((p) => p.features), truncated: false }),
      (page) => page.features.length,
      RAW_PAGE_SIZE,
    ),
  // Measured pseudosection as columnar points + a regular (x, depth) grid (property rhoa | ip | v_over_i).
  pseudosectionGrid: (datasetId, params) => api.get(`/api/datasets/${datasetId}/raw/pseudosection/grid`, { params, timeout: 120000 }),
  readings: (datasetId, params) => api.get(`/api/datasets/${datasetId}/raw/readings`, { params }),
  // Resistivity QC (backend/qc_api.py): run (stores metadata.qc, returns the full report) / stored summary.
  runQc: (datasetId, body = {}) => api.post(`/api/datasets/${datasetId}/raw/qc`, body, { timeout: 120000 }),
  getQc: (datasetId, params) => api.get(`/api/datasets/${datasetId}/raw/qc`, { params }),
};

export const statsApi = {
  getDashboard: (organizationId) => api.get('/api/stats/dashboard', { params: { organization_id: organizationId } }),
};

export const methodTypesApi = {
  list: () => api.get('/api/method-types'),
};

// Survey data APIs
export const surveyLinesApi = {
  create: (data) => api.post('/api/survey-lines', data),
  list: (projectId) => listAll('/api/survey-lines', projectId),
  geojson: (projectId) => geojsonAll('/api/survey-lines/geojson', projectId),
  delete: (lineId) => api.delete(`/api/survey-lines/${lineId}`),
};

export const soundingsApi = {
  create: (data) => api.post('/api/soundings', data),
  list: (projectId) => listAll('/api/soundings', projectId),
  geojson: (projectId) => geojsonAll('/api/soundings/geojson', projectId),
  delete: (soundingId) => api.delete(`/api/soundings/${soundingId}`),
};

export const anomaliesApi = {
  create: (data) => api.post('/api/anomalies', data),
  list: (projectId) => listAll('/api/anomalies', projectId),
  geojson: (projectId) => geojsonAll('/api/anomalies/geojson', projectId),
  delete: (anomalyId) => api.delete(`/api/anomalies/${anomalyId}`),
};

// Geostatistical analysis APIs
export const analysisApi = {
  idw: (data) => api.post('/api/analysis/idw', data),
  kriging: (data) => api.post('/api/analysis/kriging', data),
  gridding: (data) => api.post('/api/analysis/gridding', data),
  variogram: (data) => api.post('/api/analysis/variogram', data),
  cluster: (data) => api.post('/api/analysis/cluster', data),
  gridProfile: (data) => api.post('/api/analysis/grid-profile', data),
  temporalDiff: (data) => api.post('/api/analysis/temporal-diff', data),
  health: () => api.get('/api/analysis/health'),
  // Spatial autocorrelation (G47)
  moransI: (data) => api.post('/api/analysis/morans-i', data),
  lisa: (data) => api.post('/api/analysis/lisa', data),
  getisOrd: (data) => api.post('/api/analysis/getis-ord', data),
};

// PostGIS spatial RPCs (migration 010) — index-backed server-side queries
export const spatialApi = {
  featuresInView: (minLon, minLat, maxLon, maxLat, maxFeatures = 2000) =>
    api.get('/api/spatial/features-in-view', {
      params: { min_lon: minLon, min_lat: minLat, max_lon: maxLon, max_lat: maxLat, max_features: maxFeatures },
    }),
  nearby: (lat, lon, k = 10) => api.get('/api/spatial/nearby', { params: { lat, lon, k } }),
  featuresInPolygon: (geometry, maxFeatures = 2000) =>
    api.post('/api/spatial/features-in-polygon', { geometry, max_features: maxFeatures }),
  convexHull: (datasetIds) => api.post('/api/spatial/convex-hull', { dataset_ids: datasetIds }),
};

// Georeferencing (one GeoRef per dataset) + electrode geometry (Remapper port).
// docs/GEOREFERENCING_AND_MODEL_IMPORT.md
export const georefApi = {
  get: (datasetId) => api.get(`/api/datasets/${datasetId}/georef`),
  save: (datasetId, spec) => api.put(`/api/datasets/${datasetId}/georef`, spec),
  preview: (datasetId, spec) => api.post(`/api/datasets/${datasetId}/georef/preview`, spec),
  remap: (datasetId, body) => api.post(`/api/datasets/${datasetId}/geometry/remap`, body, { responseType: 'text' }),
  exportGeometry: (datasetId, format, trnFormat) =>
    api.get(`/api/datasets/${datasetId}/geometry/export`, {
      params: trnFormat ? { format, trn_format: trnFormat } : { format }, responseType: 'blob',
    }),
};

// Topography from the project DEM along the survey line (backend/topography_api.py).
export const topographyApi = {
  fromDem: (datasetId, body) => api.post(`/api/datasets/${datasetId}/topography/from-dem`, body),
  get: (datasetId) => api.get(`/api/datasets/${datasetId}/topography`),
  clear: (datasetId) => api.delete(`/api/datasets/${datasetId}/topography`),
};

// CRS lookup (pyproj): name, units, is_projected for any EPSG code the server knows.
export const crsApi = {
  lookup: (code) => api.get(`/api/crs/${encodeURIComponent(code)}`),
};

// Address / place search (backend /api/geocode): [{ label, lon, lat, bbox: [w, s, e, n] | null, source }].
// 400 for queries under 3 characters, 503 when the geocoder is down.
export const geocodeApi = {
  search: (q, limit = 5) => api.get('/api/geocode', { params: { q, limit } }).then(r => r.data.results),
};

// Boreholes (docs/BOREHOLES.md): collars + lithology intervals + log curves, migration 028.
export const boreholesApi = {
  // { project_id?, dataset_id?, bbox?: 'minLon,minLat,maxLon,maxLat', include_intervals?, limit? }
  list: (params) => api.get('/api/boreholes', { params }),
  get: (id) => api.get(`/api/boreholes/${id}`),
  create: (body) => api.post('/api/boreholes', body),
  update: (id, body) => api.patch(`/api/boreholes/${id}`, body),
  remove: (id) => api.delete(`/api/boreholes/${id}`),
  // { line: 'lon,lat;lon,lat', buffer?, project_id?, include_logs? } -> holes with chainage/fraction/offset
  nearLine: (params) => api.get('/api/boreholes/near-line', { params }),
  lithologyColors: () => api.get('/api/boreholes/lithology-colors'),
  importDataset: (datasetId, opts = {}) =>
    api.post('/api/boreholes/import', { dataset_id: datasetId, ...opts }, { timeout: 120000 }),
  importFile: (file, projectId, opts = {}) => {
    const form = new FormData();
    form.append('file', file);
    form.append('project_id', projectId);
    Object.entries(opts).forEach(([k, v]) => { if (v !== undefined && v !== null) form.append(k, v); });
    return api.post('/api/boreholes/import', form, { timeout: 120000 });
  },
};

// Report Builder templates stored per organization (backend/reports_api.py, migration 026).
export const reportsApi = {
  list: (params) => api.get('/api/reports', { params }),
  get: (id) => api.get(`/api/reports/${id}`),
  create: (body) => api.post('/api/reports', body),
  update: (id, body) => api.put(`/api/reports/${id}`, body),
  remove: (id) => api.delete(`/api/reports/${id}`),
};

// Inversion results: parse-only (the map panel keeps models client-side).
// Large 3D .out logs take a few seconds to upload and parse.
export const inversionApi = {
  parse: (body) => api.post('/api/inversion/parse', body, { timeout: 120000 }),
  formats: () => api.get('/api/inversion/formats'),
  // Stored models only (a model with a backend id): DOI surface as GeoJSON
  doi: (id, threshold) => api.get(`/api/inversion/${id}/doi`, { params: { threshold } }),
  get: (id) => api.get(`/api/inversion/${id}`, { timeout: 120000 }),
  // Layer-property map of a stored model (body: lib/inversion-results.js themeRequestBody)
  theme: (id, body) => api.post(`/api/inversion/${id}/theme`, body),
  // B - A of two stored models: 200 + model, or 202 + job id for large ones
  timelapseDiff: (body) => api.post('/api/inversion/timelapse-diff', body, { timeout: 120000 }),
  // Stored models of the caller's organizations (id, name, project_id, dataset_id, meta fields spread);
  // params: { project_id?, dataset_id? }.
  list: (params) => api.get('/api/inversion', params ? { params } : undefined),
  // Parse + store a model (project scoped): 200 { id, name, ...meta }, or 202 { job_id } for large files.
  import: (body) => api.post('/api/inversion/import', body, { timeout: 120000 }),
  setGeoref: (id, georef) => api.put(`/api/inversion/${id}/georef`, { georef }, { timeout: 120000 }),
  remove: (id) => api.delete(`/api/inversion/${id}`),
  // Final misfit / model norm / damping of several stored models (same org) for the L-curve.
  // Measured vs calculated vs misfit of a stored model (positions from its linked raw dataset when it matches).
  datafit: (id, params) => api.get(`/api/inversion/${id}/datafit`, { params, timeout: 120000 }),
  // The same for a model that only lives in the browser: { data_fit, electrodes, rms_percent, dataset_id }.
  datafitFor: (body) => api.post(`/api/inversion/datafit`, body, { timeout: 120000 }),
  lcurve: (modelIds) => api.get('/api/inversion/lcurve', { params: { model_ids: modelIds.join(',') } }),
};

// Public deployment config (version + feature flags).
export const configApi = {
  get: () => api.get('/api/config'),
};
